import React, { useState, useEffect } from "react";
import RichTextEditor from "@/components/HtmlEditor/RichTextEditor";
import styles from "../../Challenges.module.scss";
import toast from "react-hot-toast";
import { fetchDatabaseSchemasAPI } from "@/services/api/DatabaseSchema";
import { fetchAllTagsAPI } from "@/services/api/Question";
import ReactSelect from "react-select";

interface CustomTag {
    id: number;
    name: string;
}

interface DBModalProps {
    onClose: () => void;
    onUpload: (data: any) => void;
    editItem?: any | null;
}

// ─── DBModal ──────────────────────────────────────────────────────────────────
const DBModal = ({ onClose, onUpload, editItem }: DBModalProps) => {
    const isEditMode = !!editItem;
    const [id, setId] = useState<number | null>(null);
    const [name, setName] = useState("");
    const [difficulty, setDifficulty] = useState("Easy");
    const [shortDesc, setShortDesc] = useState("");
    const [description, setDescription] = useState("");
    const [expectedQuery, setExpectedQuery] = useState("");
    const [schemas, setSchemas] = useState<any[]>([]);
    const [selectedSchemaId, setSelectedSchemaId] = useState<number | string>("");
    const [allTags, setAllTags] = useState<CustomTag[]>([]);
    const [selectedTags, setSelectedTags] = useState<number[]>([]);

    const [tagSearch, setTagSearch] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => {
            getTags(tagSearch);
        }, 800);
        return () => clearTimeout(handler);
    }, [tagSearch]);

    const getTags = async (search: string = "") => {
        try {
            const res = await fetchAllTagsAPI(0, 100, "", search);
            const tagsList = res?.data?.results || res?.data || [];
            setAllTags(Array.isArray(tagsList) ? tagsList : []);
        } catch (err) {
            console.error("Tags load failed", err);
        }
    };

    const handleTagSelect = (tagId: number) => {
        if (!tagId || selectedTags.length >= 5 || selectedTags.includes(tagId)) return;
        setSelectedTags(prev => [...prev, tagId]);
    };

    const removeTag = (tagId: number) => {
        setSelectedTags(prev => prev.filter(id => id !== tagId));
    };

    useEffect(() => {
        const loadSchemas = async () => {
            try {
                const res = await fetchDatabaseSchemasAPI();
                setSchemas(res?.data || res || []);
            } catch (err) {
                console.error("Failed to load schemas:", err);
            }
        };
        loadSchemas();
    }, []);

    useEffect(() => {
        if (editItem) {
            const data = editItem?.data || editItem;
            setId(data.id || null);
            setName(data.title || "");
            setShortDesc(data.shortDescription || "");
            setDescription(data.description || "");
            setExpectedQuery(data.expectedQuery || "");
            setSelectedSchemaId(data.dbSchemaId || "");

            if (data?.difficultyLevel) {
                const diff = String(data.difficultyLevel).toLowerCase();
                setDifficulty(diff.charAt(0).toUpperCase() + diff.slice(1));
            }

            if (data.tags && Array.isArray(data.tags)) {
                setSelectedTags(data.tags.map((t: any) => t.id));
            }
        }
    }, [editItem]);

    const handleUpload = () => {
        if (!name.trim()) {
            toast.error("Challenge name is required");
            return;
        }

        const payload: any = {
            title: name,
            tagsId: selectedTags,
            shortDescription: shortDesc,
            description,
            expectedQuery,
            difficultyLevel: String(difficulty || "Easy").toUpperCase(),
            challengeType: "SQL",
            dbSchemaId: selectedSchemaId ? Number(selectedSchemaId) : null,
        };

        if (isEditMode && id) {
            payload.id = id;
        }

        onUpload(payload);
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <span className={styles.modalHeaderTitle}>
                        {isEditMode ? "Edit sql custom challenge" : "New sql custom challenge"}
                    </span>
                    <button onClick={onClose} className={styles.closeBtn}>✕</button>
                </div>

                {/* Body */}
                <div className={styles.modalBody}>
                    <label htmlFor="name" className={styles.label}>Name :</label>
                    <input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Custom challenge name"
                        className={styles.input}
                    />

                    <div className={styles.row}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Difficulty:</label>
                            <select
                                value={difficulty}
                                onChange={e => setDifficulty(e.target.value)}
                                className={styles.select}
                            >
                                <option>Easy</option>
                                <option>Medium</option>
                                <option>Hard</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.fullRow} style={{ marginBottom: 16, marginTop: 16 }}>
                        <label className={styles.label}>Tags (max 5)</label>

                        <ReactSelect
                            inputValue={tagSearch}
                            onInputChange={(val, { action }) => {
                                if (action === 'input-change') setTagSearch(val);
                            }}
                            onChange={(selected: any) => {
                                if (selected) {
                                    handleTagSelect(selected.value);
                                    setTagSearch("");
                                }
                            }}
                            options={allTags
                                .filter(tag => !selectedTags.includes(tag.id))
                                .map((tag) => ({ label: tag.name, value: tag.id }))}
                            placeholder="Search and select tags..."
                            value={null}
                            menuPortalTarget={document.body}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: 'var(--surface-2, #f8fafc)',
                                    borderColor: 'var(--border, #cbd5e1)',
                                    borderRadius: '8px',
                                    boxShadow: 'none',
                                    color: 'var(--text, #1e293b)',
                                    '&:hover': { borderColor: 'var(--accent, #3b82f6)' }
                                }),
                                input: (base) => ({
                                    ...base,
                                    color: 'var(--text, #1e293b)',
                                    margin: 0,
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: 'var(--text, #1e293b)',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: 'var(--text-3, #94a3b8)',
                                }),
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                                menu: (base) => ({
                                    ...base,
                                    backgroundColor: 'var(--surface, #ffffff)',
                                    border: '1px solid var(--border, #e2e8f0)',
                                    zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected 
                                        ? 'var(--accent, #3b82f6)' 
                                        : state.isFocused 
                                            ? 'var(--surface-2, #f1f5f9)' 
                                            : 'var(--surface, #ffffff)',
                                    color: state.isSelected 
                                        ? '#ffffff' 
                                        : 'var(--text, #1e293b)',
                                    cursor: 'pointer',
                                    '&:active': {
                                        backgroundColor: 'var(--accent, #3b82f6)'
                                    }
                                })
                            }}
                        />

                        <div className={styles.tags} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {selectedTags.map((id) => {
                                const tag = allTags.find(t => t.id === id);
                                if (!tag) return null;
                                return (
                                    <span key={tag.id} className={styles.tagBadge} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e2e8f0', padding: '4px 8px', borderRadius: '4px' }}>
                                        {tag.name}
                                        <span style={{ cursor: 'pointer' }} onClick={() => removeTag(tag.id)}>✕</span>
                                    </span>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.label}>Short description:</label>
                        <input
                            value={shortDesc}
                            onChange={e => setShortDesc(e.target.value)}
                            placeholder="Optional short description about this challenge..."
                            className={styles.input}
                            style={{ marginTop: 4 }}
                        />
                    </div>

                    <div className={styles.fieldGroup} style={{ marginBottom: 16 }}>
                        <label htmlFor="dbDescription" className={styles.label}>Heading :</label>
                        <RichTextEditor value={description} onChange={setDescription} />
                    </div>

                    <div className={styles.fieldGroup} style={{ marginBottom: 16 }}>
                        <label htmlFor="expectedQuery" className={styles.label}>Expected Query :</label>
                        <textarea
                            id="expectedQuery"
                            value={expectedQuery}
                            onChange={e => setExpectedQuery(e.target.value)}
                            placeholder="Write the expected SQL query here..."
                            className={styles.input}
                            style={{ 
                                marginTop: 4, 
                                fontFamily: 'monospace', 
                                minHeight: '120px', 
                                resize: 'vertical',
                                lineHeight: '1.5'
                            }}
                        />
                    </div>

                    {/* Main SQL table section */}
                    <div className={styles.fieldGroup} style={{ marginTop: 16 }}>
                        <label className={styles.label}>Main SQL table (Database Schema):</label>
                        <select
                            value={selectedSchemaId}
                            onChange={e => setSelectedSchemaId(e.target.value)}
                            className={styles.select}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", marginTop: "4px" }}
                        >
                            <option value="">-- Select custom SQL table --</option>
                            {schemas.map(schema => (
                                <option key={schema.id} value={schema.id}>
                                    {schema.name} ({schema.dbType})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.modalFooter}>
                    <button onClick={handleUpload} className={styles.uploadBtn}>
                        {isEditMode ? "Save Changes" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DBModal;